import { type FC } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import { CourseGroupingDropdown } from "./CourseGroupingDropdown";

import { COURSE_ID_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi } from "store/index";
import { courseSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsActions } from "utils/routing";

type AddOrRenameCourseModalProps = {
  courseId?: string;
  defaultGroupingId?: string | null;
  eventPrefix: string;
  onClose: () => void;
};

type AddOrRenameCourseFormValues = {
  code: string;
  title: string;
  groupingId: string | null;
};

export const AddOrRenameCourseModal: FC<AddOrRenameCourseModalProps> = ({
  courseId,
  defaultGroupingId,
  eventPrefix,
  onClose,
}) => {
  const course = useObjectSelector(
    (state) => courseSelectors.selectById(state, courseId),
    ["id", "code", "title"],
  );
  const form = useForm<AddOrRenameCourseFormValues>({
    defaultValues: {
      code: course?.code ?? "",
      title: course?.title ?? "",
      groupingId: defaultGroupingId || null,
    },
  });
  const { setQueryParams } = useQueryParamsActions();

  const groupingId = form.watch("groupingId");

  const onSubmit: SubmitHandler<AddOrRenameCourseFormValues> = async ({
    code: submittedCode,
    title,
    groupingId: submittedGroupingId,
  }) => {
    const trimmedCode = submittedCode.trim();
    const trimmedTitle = title.trim();

    if (course) {
      await storeApi.courses.partial_update({
        id: course.id,
        code: trimmedCode,
        title: trimmedTitle,
      });
      toastSuccessMessage(t("home.toasts.course_updated"));
    } else {
      const newCourse = await storeApi.courses.create({
        grouping_id: submittedGroupingId,
        code: trimmedCode,
        title: trimmedTitle,
      });
      toastSuccessMessage(t("home.toasts.course_added"));
      setQueryParams({ [COURSE_ID_QUERY_PARAM]: newCourse.id });
    }

    onClose();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <FormField label={t("home.courses.course_code_label")} name="code" required>
            {(controlProps) => (
              <TextField
                {...form.register("code", {
                  required: t("error.field_required"),
                  validate: (value) => value.trim().length > 0 || t("error.field_required"),
                })}
                {...controlProps}
                eventName={`${eventPrefix} / Edit Code`}
                placeholder={t("home.placeholder.course_code")}
                autoFocus
              />
            )}
          </FormField>

          <FormField label={t("glossary.course_title")} name="title">
            {(controlProps) => (
              <TextField
                {...form.register("title")}
                {...controlProps}
                eventName={`${eventPrefix} / Edit Title`}
                placeholder={t("home.placeholder.course_title")}
              />
            )}
          </FormField>

          {!course && (
            <FormField label={t("home.courses.add_course_to_grouping_label")} name="groupingId">
              {(controlProps) => (
                <CourseGroupingDropdown
                  {...controlProps}
                  selectedGroupingId={groupingId}
                  onSelectionChange={(id) => form.setValue("groupingId", id, { shouldDirty: true })}
                />
              )}
            </FormField>
          )}
        </div>

        <div className="mt-8 flex w-full justify-end gap-2">
          <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton
            disabled={!!course && !form.formState.isDirty}
            kind="accent"
            size="m"
            title={course ? t("common.save_changes") : t("common.add.course")}
            type="submit"
          >
            {course ? t("common.save_changes") : t("common.add.course")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
